import { useState, useEffect, useRef } from "react";
import { useSearch } from "../hooks/useSearch";

export default function SearchBox({
  value,
  onChange,
  onSearch,
  onResults,
}) {
  const [internal, setInternal] = useState(value ?? "");
  // yeni: kullanıcının gerçekten "arama başlattığını" takip etmek için
  const [searched, setSearched] = useState(false);
  const {
    setQuery,
    triggerNow,
    results,
    loading,
    error,
    navigateToNews,
    navigateToService,
  } = useSearch();

  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const resultsRef = useRef(null); // yeni: sonuç kartı ref'i
  const bodyOverflowRef = useRef(null); // önceki body overflow değerini saklamak için
  const bodyPositionRef = useRef(null); // önceki body position
  const bodyTopRef = useRef(null); // kaydırma pozisyonunu saklamak için
  // otomatik arama debounce ve uyarı için
  const debounceRef = useRef(null);
  const autoNoticeTimeoutRef = useRef(null);
  const MIN_AUTO_CHARS = 3;
  const [autoNotice, setAutoNotice] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [activeTab, setActiveTab] = useState(null); // başlangıçta hiç sekme seçili değil

  // Animated placeholder examples
  const examplePlaceholders = [
    "Talep, istek, teşekkür ve şikayet formu",
    "Nöbetçi Eczaneler",
    "Vergi Ödeme",
    "Kent Rehberi",
    "İmar Durumu",
    "Duyurular",
    "Hizmetlerimiz",
    "İletişim",
    "En Yakın Pazar Yeri"
  ];
  const [animatedPlaceholder, setAnimatedPlaceholder] = useState(examplePlaceholders[0]);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [typing, setTyping] = useState(true);
  const [charIndex, setCharIndex] = useState(0);

  // Typing/deleting effect
  useEffect(() => {
    let timeout;
    const currentText = examplePlaceholders[placeholderIndex];
    if (typing) {
      if (charIndex < currentText.length) {
        timeout = setTimeout(() => setCharIndex(charIndex + 1), 80);
      } else {
        timeout = setTimeout(() => setTyping(false), 1500);
      }
    } else {
      if (charIndex > 0) {
        timeout = setTimeout(() => setCharIndex(charIndex - 1), 30);
      } else {
        setTyping(true);
        setPlaceholderIndex((placeholderIndex + 1) % examplePlaceholders.length);
      }
    }
    setAnimatedPlaceholder(currentText.slice(0, charIndex));
    return () => clearTimeout(timeout);
  }, [charIndex, typing, placeholderIndex]);

  // kilitle body kaydırması
  const lockBodyScroll = () => {
    try {
      if (typeof document !== "undefined" && document.body) {
        // sakla önceki değerler
        bodyOverflowRef.current = document.body.style.overflow;
        bodyPositionRef.current = document.body.style.position;
        bodyTopRef.current = document.body.style.top;
        const scrollY = window.scrollY || window.pageYOffset || 0;
        // lock: gizle overflow ve sabitle body pozisyonu, böylece arkaplan kaymaz
        document.body.style.overflow = "hidden";
        document.body.style.position = "fixed";
        document.body.style.top = `-${scrollY}px`;
      }
    } catch {
      // ignore errors
    }
  };
  // serbest bırak
  const unlockBodyScroll = () => {
    try {
      if (typeof document !== "undefined" && document.body) {
        // geri yükle önceki stiller
        document.body.style.overflow = bodyOverflowRef.current || "";
        document.body.style.position = bodyPositionRef.current || "";
        document.body.style.top = bodyTopRef.current || "";
        // sayfayı kilitlediğimiz yerdeki scroll pozisyonına geri çek
        const prevTop = bodyTopRef.current;
        if (prevTop && prevTop.startsWith("-")) {
          const scrollY = parseInt(prevTop.replace("-", "").replace("px", "")) || 0;
          window.scrollTo(0, scrollY);
        }
        bodyOverflowRef.current = null;
        bodyPositionRef.current = null;
        bodyTopRef.current = null;
      }
    } catch {
      // ignore errors
    }
  };
  // cleanup on unmount
  useEffect(() => {
    return () => {
      // temizle zamanlayıcılar
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      if (autoNoticeTimeoutRef.current) {
        clearTimeout(autoNoticeTimeoutRef.current);
        autoNoticeTimeoutRef.current = null;
      }
      unlockBodyScroll();
    };
  }, []);

  // showResults true olduğunda body kaydırmasını kilitle, false olduğunda aç
  useEffect(() => {
    if (showResults) {
      lockBodyScroll();
    } else {
      unlockBodyScroll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showResults]);

  // showResults false olduğunda garantili unlock
  // useEffect(() => {
  //   if (!showResults) unlockBodyScroll();
  // }, [showResults]);

  useEffect(() => {
    if (value !== undefined && value !== internal) {
      setInternal(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // eğer input boşalırsa "search performed" durumunu sıfırla
  useEffect(() => {
    if ((internal ?? "").trim() === "") {
      setSearched(false);
    }
  }, [internal]);

  useEffect(() => {
    if (typeof onResults === "function") {
      onResults(results);
    }
    // eğer gösterimdeysek ve aktif sekme boşsa veya aktif sekmede sonuç kalmadıysa,
    // içinde sonuç olan ilk sekmeyi aktif yap; sonuç yoksa aktifTab null kalır
    const counts = {
      news: (results && results.news && results.news.length) || 0,
      events: (results && results.events && results.events.length) || 0,
      announcements: (results && results.announcements && results.announcements.length) || 0,
      services: (results && results.services && results.services.length) || 0,
    };
    if (showResults) {
      const order = ["news", "events", "announcements", "services"];
      if (!activeTab || !counts[activeTab]) {
        const firstWith = order.find((k) => counts[k] > 0) || null;
        setActiveTab(firstWith);
      }
    }
  }, [results, onResults, showResults, activeTab]);

  const handleChange = (e) => {
    const v = e.target.value;
    // önceki debounce'u temizle
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    // temizlenmişse bekleyen otomatik uyarıyı da iptal et
    if (autoNoticeTimeoutRef.current) {
      clearTimeout(autoNoticeTimeoutRef.current);
      autoNoticeTimeoutRef.current = null;
    }

    setInternal(v);
    if (typeof onChange === "function") onChange(v);
    const trimmed = (v ?? "").trim();

    // hiç giriş yok -> arama durumunu sıfırla ve bildirim kaldır; sonuç kutusunu kapat
    if (trimmed.length === 0) {
      setSearched(false);
      setAutoNotice("");
      setShowResults(false);
      return;
    }

    // 1-2 karakter -> kullanıcıyı uyar ve sonuç kartını aç
    if (trimmed.length < MIN_AUTO_CHARS) {
      setSearched(false);
      setAutoNotice(`En az ${MIN_AUTO_CHARS} karakter giriniz`);
      setShowResults(true); // Kartı aç
      return;
    }

    // >= MIN_AUTO_CHARS -> debounce ile otomatik arama tetikle
    setAutoNotice(""); // Yeterli karakter varsa uyarıyı temizle
    setShowResults(true);
    debounceRef.current = setTimeout(() => {
      // setQuery de burada yapılır, böylece useSearch gerçek arama başlatır
      setQuery(v);
      triggerNow(v);
      setSearched(true);
      setAutoNotice(`${MIN_AUTO_CHARS}+ karakter: otomatik arama yapıldı`);
      if (autoNoticeTimeoutRef.current) clearTimeout(autoNoticeTimeoutRef.current);
      autoNoticeTimeoutRef.current = setTimeout(() => setAutoNotice(""), 2000);
      debounceRef.current = null;
    }, 400);
  };

  const doSearchNow = (q) => {
    // manuel aramada varsa pending otomatik aramayı iptal et ve bildirimleri temizle
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (autoNoticeTimeoutRef.current) {
      clearTimeout(autoNoticeTimeoutRef.current);
      autoNoticeTimeoutRef.current = null;
    }
    setAutoNotice("");
    const term = (q === undefined || q === null) ? internal : q;
    // Manuel aramada da setQuery gerekli (useSearch ile uyumlu)
    setQuery(term);
    if (typeof onSearch === "function") onSearch(term);
    triggerNow(term);
    setShowResults(true);
    // kullanıcının bir arama başlattığını işaretle
    setSearched(true);
    // arama tetiklendiğinde önce aktif sekmeyi temizle; sonuçlar geldikçe efekt uygun sekmeyi atayacak
    setActiveTab(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") doSearchNow();
  };

  // click outside to close results
  useEffect(() => {
    const onDocClick = (ev) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(ev.target)) {
        // sonuçları kapatırken body kilidini kaldır
        unlockBodyScroll();
        setShowResults(false);
      }
    };
    document.addEventListener("click", onDocClick);
    return () => {
      document.removeEventListener("click", onDocClick);
    };
  }, []);

  // Swiper izolasyonu için native event listener'lar (Kesin Çözüm)
  useEffect(() => {
    const card = resultsRef.current;
    if (!card || !showResults) return;

    const stopPropagation = (e) => e.stopPropagation();
    
    // Bu olayların yukarı (Swiper'a) ulaşmasını native seviyede engelle
    const events = ["wheel", "touchstart", "touchmove", "touchend", "mousedown"];
    events.forEach(ev => card.addEventListener(ev, stopPropagation, { passive: false }));

    return () => {
      events.forEach(ev => card.removeEventListener(ev, stopPropagation));
    };
  }, [showResults]);

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      if (isNaN(d)) return iso;
      return d.toLocaleDateString("tr-TR", { year: "numeric", month: "short", day: "numeric" });
    } catch {
      return iso;
    }
  };

  // minimal helper renderers (concise, use existing logic)
  const renderNews = (news = []) =>
    news.map((n) => (
      <div
        key={`news-${n.id}`}
        className="result-item"
        onClick={() => (navigateToNews ? navigateToNews(n.slug) : (window.location.href = `/haberler/${n.slug}`))}
        role="button"
        tabIndex={0}
      >
        {n.kapakUrl ? <img src={n.kapakUrl} alt={n.baslik || "haber"} className="result-thumb" /> : <div className="result-thumb placeholder" />}
        <div className="result-body">
          <div className="result-title">{n.baslik}</div>
          {n.aciklama ? <div className="result-sub">{n.aciklama}</div> : null}
        </div>
      </div>
    ));

  const renderEvents = (events = []) =>
    events.map((e) => (
      <div
        key={`event-${e.id}`}
        className="result-item"
        onClick={() => { const slug = e.slug || ""; if (slug) window.location.href = `/etkinlik/${slug}`; }}
        role="button"
        tabIndex={0}
      >
        {e.imageUrl ? <img src={e.imageUrl} alt={e.name || "etkinlik"} className="result-thumb" /> : <div className="result-thumb placeholder" />}
        <div className="result-body">
          <div className="result-title">{e.name}</div>
        </div>
      </div>
    ));

  const renderServices = (services = []) =>
    services.map((s) => (
      <div
        key={`service-${s.id}`}
        className="result-item"
        onClick={() => (navigateToService ? navigateToService(s) : (s.url ? window.location.href = s.url : window.location.href = `/hizmetlerimiz/${s.slug}`))}
        role="button"
        tabIndex={0}
      >
        {s.imageUrl ? <img src={s.imageUrl} alt={s.adi || "hizmet"} className="result-thumb" /> : <div className="result-thumb placeholder" />}
        <div className="result-body">
          <div className="result-title">{s.adi}</div>
        </div>
      </div>
    ));

  const renderAnnouncements = (anns = []) =>
    anns.map((a) => (
      <div key={`ann-${a.id}`} className="result-item">
        <div className="result-body">
          <div className="result-title">{a.baslik}</div>
          <div className="result-sub">
            {a.duyuru_turu ? <span className="tag">{a.duyuru_turu}</span> : null}
            {a.baslangic_tarihi ? <span className="muted"> {fmtDate(a.baslangic_tarihi)}</span> : null}
          </div>
        </div>
      </div>
    ));

  const hasAnyResults =
    !!(
      (results && results.news && results.news.length) ||
      (results && results.events && results.events.length) ||
      (results && results.services && results.services.length) ||
      (results && results.announcements && results.announcements.length)
    );

  // sekme başlıkları için yardımcı: her sekmenin label ve count'u
  const tabInfo = [
    { key: "news", label: "Haberler", count: (results && results.news && results.news.length) || 0 },
    { key: "events", label: "Etkinlikler", count: (results && results.events && results.events.length) || 0 },
    { key: "announcements", label: "Duyurular", count: (results && results.announcements && results.announcements.length) || 0 },
    { key: "services", label: "Hizmetler & Sayfalar", count: (results && results.services && results.services.length) || 0 },
  ];

  const renderActiveSection = () => {
    if (!results || !activeTab) return null;
    switch (activeTab) {
      case "news": return (!loading && results.news && results.news.length) ? (
        <div className="results-section">{renderNews(results.news)}</div>
      ) : null;
      case "events": return (!loading && results.events && results.events.length) ? (
        <div className="results-section">{renderEvents(results.events)}</div>
      ) : null;
      case "services": return (!loading && results.services && results.services.length) ? (
        <div className="results-section">{renderServices(results.services)}</div>
      ) : null;
      case "announcements": return (!loading && results.announcements && results.announcements.length) ? (
        <div className="results-section">{renderAnnouncements(results.announcements)}</div>
      ) : null;
      default: return null;
    }
  };

  // render
  return (
    // containerRef keeps track for outside clicks; inputRef used to compute position
    <div className="search-container" ref={containerRef} style={{ position: "relative"}}>
      <input
        ref={inputRef}
        type="search"
        placeholder={animatedPlaceholder || "Sitede ne aramak istersiniz?"}
        className="search-input"
        value={internal}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        // sadece input doluysa veya daha önce arama yapılmışsa sonuç kutusunu göster
        onFocus={() => {
          const t = (internal ?? "").trim();
          if (searched || (t.length >= MIN_AUTO_CHARS)) {
            setShowResults(true);
          }
        }}
        aria-label="site-search"
      />
      {/* otomatik arama uyarısını kaldır - artık kart içinde gösterilecek */}
      <button
        className="search-button"
        onClick={() => doSearchNow()}
        disabled={loading}
      >
        {loading ? "Aranıyor..." : "Ara"}
      </button>
      {error ? <div className="search-error">{error}</div> : null}

      {showResults && (
        <div
          className="search-results-card swiper-no-swiping"
          ref={resultsRef}
        >
          {/* Mobile Close Bar */}
          <div className="mobile-close-bar">
             <span className="mobile-search-term">
               {internal ? `"${internal}"` : "Arama"}
             </span>
             <button 
               className="mobile-close-btn"
               onClick={(e) => {
                 e.stopPropagation();
                 setShowResults(false);
               }}
             >
               Kapat ✕
             </button>
          </div>

          {/* Karakter sayısı yetersizse uyarı göster */}
          {(internal ?? "").trim().length > 0 && (internal ?? "").trim().length < MIN_AUTO_CHARS ? (
            <div className="search-status" style={{ color: "#6b7280", textAlign: "center" }}>
              {autoNotice}
            </div>
          ) : (
            <>
              {/* Sekme başlıkları - sadece count>0 olan sekmeler gösterilecek */}
              {tabInfo.some(t => t.count > 0) ? (
                <div className="results-tabs" role="tablist">
                  {tabInfo.filter(t => t.count > 0).map((t) => (
                    <button
                      key={t.key}
                      role="tab"
                      aria-selected={activeTab === t.key}
                      className={`tab ${activeTab === t.key ? "active" : ""}`}
                      onClick={() => setActiveTab(t.key)}
                    >
                      {t.label} <span className="tab-count">({t.count})</span>
                    </button>
                  ))}
                </div>
              ) : null}
              {loading && !hasAnyResults ? <div className="search-status">Arama yapılıyor...</div> : null}
              {!loading && !hasAnyResults && (searched || (internal && internal.trim().length > 0)) ? (
                <div className="search-status">Sonuç yok</div>
              ) : null}

              {/* Aktif sekmeyi göster (aktif sekme yoksa burada bir şey render edilmez) */}
              {renderActiveSection()}
            </>
          )}
        </div>
      )}
    </div>
  );
}